import { MenuItem, Typography } from '@material-ui/core';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Alert, AlertTitle } from '@material-ui/lab';
import {
  mdiArrowRightBottom,
  mdiCheck,
  mdiChevronDown,
  mdiChevronUp,
  mdiDelete,
  mdiDotsVertical,
  mdiDownload,
  mdiMap,
  mdiMapCheck,
  mdiMapMarkerRadiusOutline,
  mdiMerge,
  mdiPencil,
  mdiPlus,
  mdiSquare,
  mdiTableArrowDown,
  mdiTableArrowUp,
  mdiVectorSquareEdit,
} from '@mdi/js';
import Icon from '@mdi/react';
import ActionContainer from 'components/ActionContainer/ActionContainer';
import Button from 'components/Button/Button';
import ButtonLoading from 'components/ButtonLoading/ButtonLoading';
import ColorPickerDialog from 'components/ColorPickerDialog/ColorPickerDialog';
import DateTimePicker from 'components/DateTimePicker/DateTimePicker';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import Dialog from 'components/DialogWrapper/DialogWrapper';
import MapDialog from 'components/MapDialog/MapDialog';
import ProjectDeleteConfirmationDialog from 'components/ProjectDeleteConfirmationDialog/ProjectDeleteConfirmationDialog';
import { ProjectCompetencyTypesSection } from 'components/ProjectDialog/components/ProjectCompetencyTypesSection';
import ProjectImage from 'components/ProjectDialog/components/ProjectImage';
import { ProjectTradeRatesSection } from 'components/ProjectDialog/components/ProjectTradeRatesSection';
import { RequiredCompetencyCardsContent } from 'components/ProjectDialog/components/RequiredCompetencyCardsContent';
import TextInput from 'components/TextInput/TextInput';
import Config from 'Config';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ComboBox from 'scenes/ExpenseTracking/components/ComboBox';
import {
  PROJECT_STATUS_EDIT,
  SALARIES_TYPES_EDIT,
} from 'scenes/Permissions/constants/permissions';
import { selectIsRequiredBusinessPermissionFromCurrentActiveEmployerBusiness } from 'scenes/Permissions/store/selectors/personalPermissionsSelectors';
import { selectIsRequiredPermissionByProjectId } from 'scenes/Permissions/store/selectors/personalPermissionsSelectorsByProjectId';
import { AlarmSettings } from 'scenes/ProjectEdit/components/AlarmSettings/AlarmSettings';
import { setIsJydacomAssignmentsDialogOpen } from 'store/assignments/actions/assignments.actions';
import copyAssignments from 'store/assignments/utils/copyAssignments.util';
import { clearAllEmployerAllowanceDrafts } from 'store/employerAllowances/actions/employerAllowances.actions';
import { createEmployerAllowanceDraft } from 'store/employerAllowances/thunks/createEmployerAllowanceDraft.thunk';
import { loadEmployerAllowancesByBusinessId } from 'store/employerAllowances/thunks/loadEmployerAllowancesByBusinessId.thunk';
import { getProjectFirestopDetailAndProductItems } from 'store/firestopDocumentation/services/get-project-firestop-detail-and-product-items.service';
import { getProjectClockInAreas } from 'utils/getProjectClockInAreas';
import isProduction from 'utils/isProduction';
import uuidv4 from 'uuid/v4';
import AddressAutocomplete from '../AddressAutocomplete/AddressAutocomplete';
import AssignmentItem from './components/AssignmentItem';
import ContactAutocomplete from './components/ContactAutocomplete';
import { EmployerAllowances } from './components/EmployerAllowances/EmployerAllowances';
import { FirestopCatalogsSection } from './components/FirestopCatalogsSection';
import { ProjectStatusSelect } from './components/ProjectStatusSelect';
import SwitchItem from './components/SwitchItem';
import useProjectDialog from './hooks/useProjectDialog';
import useStyles from './ProjectDialogStyles';
import { getCoordinatesByAddress } from './utils/getCoordinatesByAddress';
import { getCoordinatesFromUser } from './utils/getCoordinatesFromUser';

const NORMAL_PROJECT_STATUS_VALUES = [
  'ESTIMATING',
  'IN_PROGRESS',
  'WARRANTY_PERIOD',
];

const getInitialProjectStatus = ({ businessTypeEnum, status, type }) => {
  if (type === 'create') {
    return 'ESTIMATING';
  }

  if (status === 'ACTIVE') {
    return 'IN_PROGRESS';
  }

  if (NORMAL_PROJECT_STATUS_VALUES.includes(status)) {
    return status;
  }

  if (businessTypeEnum === 'NORMAL' && !status) {
    return 'IN_PROGRESS';
  }

  return status || '';
};

function ProjectDialogLayout(props) {
  const {
    project,
    businessId,
    setParentProject,
    onClose,
    open,
    type,
    loading,
    parentProject,
    parentObject,
    onCreate,
    onUpdate,
    onDelete,
    projects,
    companyMode,
    customerOptions,
    openCustomerDialog,
  } = props;
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation('common');

  const upSm = useMediaQuery(theme.breakpoints.up('sm'));

  const autoClockOutOptions = [
    { value: 360, label: t('project_dialog.hour_options.6') },
    { value: 420, label: t('project_dialog.hour_options.7') },
    { value: 450, label: t('project_dialog.hour_options.7_5') },
    { value: 480, label: t('project_dialog.hour_options.8') },
    { value: 510, label: t('project_dialog.hour_options.8_5') },
    { value: 540, label: t('project_dialog.hour_options.9') },
    { value: 570, label: t('project_dialog.hour_options.9_5') },
    { value: 600, label: t('project_dialog.hour_options.10') },
    { value: 660, label: t('project_dialog.hour_options.11') },
    { value: 720, label: t('project_dialog.hour_options.12') },
    { value: 780, label: t('project_dialog.hour_options.13') },
    { value: 840, label: t('project_dialog.hour_options.14') },
    { value: 900, label: t('project_dialog.hour_options.15') },
    { value: 960, label: t('project_dialog.hour_options.16') },
    { value: 1020, label: t('project_dialog.hour_options.17') },
    { value: 1080, label: t('project_dialog.hour_options.18') },
    { value: 1140, label: t('project_dialog.hour_options.19') },
    { value: 1200, label: t('project_dialog.hour_options.20') },
  ];

  // The site manager suggestions come from the project's own people. In create
  // mode the project has no id and no resources yet, so the parent project's
  // people are the suggestion pool.
  const {
    contactOptions,
    siteManagerOptions,
    getIlveability,
    getIntegrationPartners,
    readAssignmentsFromXLS,
    writeAssignmentsToXLS,
  } = useProjectDialog(businessId, project.id || parentProject);

  const dispatch = useDispatch();

  // Closable container open-states
  const [openProjectSettings, setOpenProjectSettings] = useState(false);
  const [openProjectAddress, setOpenProjectAddress] = useState(false);
  const [openContactInformation, setOpenContactInformation] = useState(false);
  const [openManagerInformation, setOpenManagerInformation] = useState(false);
  const [openForemanListInformation, setOpenForemanListInformation] = useState(
    {}
  );
  const [openCustomerInformation, setOpenCustomerInformation] = useState(false);
  const [openAdditionalSettings, setOpenAdditionalSettings] = useState(false);
  const [openAssignments, setOpenAssignments] = useState(false);
  const [openSalaryInformation, setOpenSalaryInformation] = useState(false);
  const [openNotificationSettings, setOpenNotificationSettings] =
    useState(false);
  const [openCompetencyTypesInformation, setOpenCompetencyTypesInformation] =
    useState(false);
  const [openTradeRates, setOpenTradeRates] = useState(false);
  const [
    tradeRatesChangedFromParentWarningOpen,
    setTradeRatesChangedFromParentWarningOpen,
  ] = useState(false);

  // Whether this project is currently inheriting trade costs from its parent
  // (it has none of its own, but the parent has some). Edit-only: new projects
  // copy the parent's values up front and own them on save.
  const tradeRatesInherited = useMemo(() => {
    if (type !== 'edit') return false;
    const projectHasOwnTradeRates =
      project.tradeHourlyCosts &&
      Object.keys(project.tradeHourlyCosts).length > 0;
    const parentHasTradeRates =
      parentObject?.tradeHourlyCosts &&
      Object.keys(parentObject.tradeHourlyCosts).length > 0;
    return !projectHasOwnTradeRates && Boolean(parentHasTradeRates);
  }, [type, project, parentObject]);

  const [openColorPickerDialog, setOpenColorPickerDialog] = useState(false);
  const [openMapDialog, setOpenMapDialog] = useState(false);
  const [openDrawCircleMapDialog, setOpenDrawCircleMapDialog] = useState(false);
  const [openDrawAreaMapDialog, setOpenDrawAreaMapDialog] = useState(false);
  const [openDeleteConfirmationDialog, setOpenDeleteConfirmationDialog] =
    useState(false);

  // Project information
  const [projectImage, setProjectImage] = useState(project.picture || null);
  const [imageUpdated, setImageUpdated] = useState(false);

  const [projectParentWarningOpen, setProjectParentWarningOpen] =
    useState(false);

  const [ilveability, setIlveability] = useState(false);

  const [integrationPartners, setIntegrationPartners] = useState('');

  const [userLocation, setUserLocation] = useState(null);

  const [loadingLocation, setLocationLoading] = useState(false);

  // Assignments
  const [assignments, setAssignments] = useState(props.assignments || []);
  const [competencyTypes, setCompetencyTypes] = useState(
    (props.projectCompetencyTypes || []).map((competencyType) => ({
      ...competencyType,
      deletedAt: null,
      isNew: false,
    }))
  );
  const [hasCompetencyTypeEdits, setHasCompetencyTypeEdits] = useState(false);
  const activeAssignments = useMemo(
    () => assignments.filter((a) => !a.deletedAt),
    [assignments]
  );
  const activeCompetencyTypes = useMemo(
    () => competencyTypes.filter((competencyType) => !competencyType.deletedAt),
    [competencyTypes]
  );
  const [showAssignmentNumberPath, setShowAssignmentNumberPath] = useState(
    project.showAssignmentNumberPath || false
  );
  const [createdAssignmentIds, setCreatedAssignmentIds] = useState([]);
  const [updatedAssignmentIds, setUpdatedAssignmentIds] = useState([]);

  const [firestopDetails, setFirestopDetails] = useState([]);
  const [createdFirestopDetailIds, setCreatedFirestopDetailIds] = useState([]);
  const [updatedFirestopDetailIds, setUpdatedFirestopDetailIds] = useState([]);
  const [isLoadingFirestopDetails, setIsLoadingFirestopDetails] =
    useState(false);

  const [firestopProducts, setFirestopProducts] = useState([]);
  const [createdFirestopProductIds, setCreatedFirestopProductIds] = useState(
    []
  );
  const [updatedFirestopProductIds, setUpdatedFirestopProductIds] = useState(
    []
  );
  const [isLoadingFirestopProducts, setIsLoadingFirestopProducts] =
    useState(false);

  const [assignmentCopyInfoOpen, setAssignmentCopyInfoOpen] = useState(false);
  const [assignmentCopyWarningOpen, setAssignmentCopyWarningOpen] =
    useState(false);
  const [openAssignmentsArray, setOpenAssignmentsArray] = useState([]);

  const isPermissionToEditProjectStatus = useSelector((state) =>
    selectIsRequiredPermissionByProjectId(project.id)(
      state,
      PROJECT_STATUS_EDIT
    )
  );

  const isPermissionToEditSalaryTypes = useSelector((state) =>
    selectIsRequiredBusinessPermissionFromCurrentActiveEmployerBusiness(
      state,
      SALARIES_TYPES_EDIT
    )
  );

  const showProjectStatusField =
    !companyMode &&
    (type === 'create' || project.businessTypeEnum === 'NORMAL');

  const [projectData, setProjectData] = useState({
    // Project information
    id: project.id || uuidv4(),
    name: project.name || '',
    status: getInitialProjectStatus({
      businessTypeEnum: project.businessTypeEnum,
      status: project.status,
      type,
    }),
    // picture: project.picture || null,
    startAt: project.startAt
      ? dayjs(project.startAt)
      : type === 'create'
        ? dayjs()
        : null,
    endAt: project.endAt ? dayjs(project.endAt) : null,
    mainColor: project.mainColor || '#32c1d9',
    shortDescription: project.shortDescription || '',
    longDescription: project.longDescription || '',

    // Ilve settings
    ilve:
      project.ilve === 'yes' ||
      project.ilve === true ||
      project.ilve === 'true',
    ilmoitettava:
      project.ilve === 'yes' ||
      project.ilve === true ||
      project.ilve === 'true',
    costpoolNumber: project.costpoolNumber || '',

    // Project requirements
    assignmentRequired:
      project.assignmentRequired || parentObject.assignmentRequired || false,
    orientationRequired:
      project.orientationRequired || parentObject.orientationRequired || false,
    autoShowOrientationForm:
      project.autoShowOrientationForm ||
      parentObject.autoShowOrientationForm ||
      false,
    orientationFormRequired:
      project.orientationFormRequired ||
      parentObject.orientationFormRequired ||
      false,
    pictureMust:
      Boolean(project.pictureMust) ||
      Boolean(parentObject.pictureMust) ||
      false,
    logoCircular: Boolean(project.logoCircular) || false,
    validTaxNumberRequired:
      project.validTaxNumberRequired ||
      parentObject.validTaxNumberRequired ||
      false,
    // Per-project only — no inheritance from parent projects.
    competenciesRequired: project.competenciesRequired || false,
    requiredCompetencyCardTypes: project.requiredCompetencyCardTypes || [],
    clockImageRequired: false,
    clockInImageAmount: 0,
    clockOutImageAmount: 0,
    autoClockOut: project.autoClockOut === false ? false : true,
    enableInvoiceProjectScanning:
      project.enableInvoiceProjectScanning === false ? false : true,
    preventClockInAfterEndDate:
      project.preventClockInAfterEndDate === false ? false : true,
    autoClockOutThreshold: project.autoClockOutThreshold || 900,
    autoClockOutTrim: project.autoClockOutTrim || 510,

    // Project address
    street1: project.street1 || '',
    street2: project.street2 || '',
    co: project.co || '',
    postcode: project.postcode || '',
    city: project.city || '',
    country: project.country || '',
    locationDescription: project.locationDescription || '',

    // Location settings
    locationMust: project.locationMust || parentObject.locationMust || false,
    proximityToClockInRequired: project.proximityToClockInRequired || false,
    proximityToClockInRequiredSkipAllowed:
      project.proximityToClockInRequiredSkipAllowed ||
      parentObject.proximityToClockInRequiredSkipAllowed ||
      false,
    proximityToClockInType: project.proximityToClockInType || '', // 'area' or 'radius'

    proximityToClockInRadius: project.proximityToClockInRadius || null,

    proximityToClockInArea: project.proximityToClockInArea || [],

    proximityToClockInAreas: getProjectClockInAreas(project),

    latitude: project.latitude || null,
    longitude: project.longitude || null,

    // Contact information
    contact_first_name: project.contact_first_name || '',
    contact_last_name: project.contact_last_name || '',
    contact_email: project.contact_email || '',
    contact_phone: project.contact_phone || '',
    contact_street1: project.contact_street1 || '',
    contact_street2: project.contact_street2 || '',
    contact_co: project.contact_co || '',
    contact_postcode: project.contact_postcode || '',
    contact_city: project.contact_city || '',
    contact_country: project.contact_country || '',

    // Site manager information
    site_manager_first_name: project.site_manager_first_name || '',
    site_manager_last_name: project.site_manager_last_name || '',
    site_manager_email: project.site_manager_email || '',
    site_manager_phone: project.site_manager_phone || '',
    site_manager_user_id: project.site_manager_user_id || null,
    site_manager_street1: project.site_manager_street1 || '',
    site_manager_street2: project.site_manager_street2 || '',
    site_manager_co: project.site_manager_co || '',
    site_manager_postcode: project.site_manager_postcode || '',
    site_manager_city: project.site_manager_city || '',
    site_manager_country: project.site_manager_country || '',

    // Foremen
    foremanList: project.foremanList || [],

    // Customer information
    customer: project.customer || null,

    // Payment information
    iban: project.iban || '',
    bic: project.bic || '',

    // Integration dimensions
    fennoaProjectDimensionId: project.fennoaProjectDimensionId || '',
    talenomProjectDimensionId: project.talenomProjectDimensionId || '',
    procountorDimension: '',
    jydacomWorksiteId: project.jydacomWorksiteId || '',
    talosoftaProjectId: project.talosoftaProjectId || '',

    // Assignments
    showAssignmentNumberPath: project.showAssignmentNumberPath || false,
    hasOwnAssignments: false,

    // Clock in settings
    restrictEmployeesMultipleClockIn:
      project.restrictEmployeesMultipleClockIn ||
      parentObject.restrictEmployeesMultipleClockIn ||
      false,

    // Project bank settings
    isProjectBankFolderStructureCopied:
      project.isProjectBankFolderStructureCopied ||
      parentObject.isProjectBankFolderStructureCopied ||
      false,
    // Tilaajavastuu settings
    isTilaajavastuuDocumentsFetchedByDefault:
      project.isTilaajavastuuDocumentsFetchedByDefault ||
      parentObject.isTilaajavastuuDocumentsFetchedByDefault ||
      false,
    // Organization chart
    isOrganizationApprovalRequiredForClockIn:
      project.isOrganizationApprovalRequiredForClockIn,

    isSubcontractorsOwnEmployeeRecordEditingRestricted:
      project.isSubcontractorsOwnEmployeeRecordEditingRestricted,

    automaticMealBreakReductionLengthMinutes:
      project.automaticMealBreakReductionLengthMinutes || 30,

    // Project-specific hourly cost per construction trade.
    // Map of trade code -> hourly cost (€/h), e.g. { RAM: 38.5, TJ: 52 }.
    // New projects copy the parent's costs; existing projects without their own
    // costs fall back to (inherit) the parent's values for display.
    tradeHourlyCosts:
      project.tradeHourlyCosts &&
      Object.keys(project.tradeHourlyCosts).length > 0
        ? project.tradeHourlyCosts
        : parentObject.tradeHourlyCosts || {},
  });

  const mealBreakLengthMinutesOptions = [
    { label: '0,5h', value: 30 },
    { label: '1h', value: 60 },
  ];

  const isReportableAccessible =
    !showProjectStatusField || projectData.status === 'IN_PROGRESS';

  useEffect(() => {
    setCompetencyTypes(
      (props.projectCompetencyTypes || []).map((competencyType) => ({
        ...competencyType,
        deletedAt: null,
        isNew: false,
      }))
    );
    setHasCompetencyTypeEdits(false);
  }, [project.id]);

  useEffect(() => {
    if (hasCompetencyTypeEdits) {
      return;
    }

    setCompetencyTypes(
      (props.projectCompetencyTypes || []).map((competencyType) => ({
        ...competencyType,
        deletedAt: null,
        isNew: false,
      }))
    );
  }, [hasCompetencyTypeEdits, props.projectCompetencyTypes]);

  const handleChange = (e) => {
    if (!e) return;
    const { name, value } = e.target;
    // Picking an AddressAutocomplete suggestion also fires onInputChange,
    // and that event's target is the option element rather than the named
    // input. There is no field to set, and before the site-manager reset
    // below it wrote one called "undefined" instead of failing.
    if (!name) return;

    setProjectData((prev) => ({
      ...prev,
      [name]: value,
      // Typing over any site-manager field means this is no longer the person
      // who was picked from the project's people, so the stored identity has to
      // go with it. Leaving it would keep the previous site manager's right to
      // sign the vastaava työnjohtaja slot after they were replaced by hand.
      ...(name.startsWith('site_manager_')
        ? { site_manager_user_id: null }
        : {}),
    }));
  };

  useEffect(() => {
    if (loading) return;
    if (type === 'edit') {
      setAssignments(props.assignments);
    } else {
      const newAssignments = copyAssignments(props.assignments, projectData.id);
      setAssignmentCopyInfoOpen(true);

      setCreatedAssignmentIds(newAssignments.map((a) => a.id));
      setAssignments(newAssignments);
    }
  }, [props.assignments]);

  const handleIlmoitusvelvollinenChange = (value) => {
    setProjectData((prev) => ({
      ...prev,
      ilve: value,
      ilmoitettava: value,
    }));
    if (value) {
      setOpenContactInformation(true);
      setOpenProjectAddress(true);
    }
  };

  const handleOrientationRequiredChange = (value) => {
    setProjectData((prev) => ({
      ...prev,
      orientationRequired: value,
    }));
  };

  const handleProjectStatusChange = (value) => {
    setProjectData((prev) => ({
      ...prev,
      status: value,
      ...(value === 'ESTIMATING' && {
        ilve: false,
        ilmoitettava: false,
      }),
    }));
  };

  const handleAutoShowOrientationFormChange = (value) => {
    setProjectData((prev) => ({
      ...prev,
      autoShowOrientationForm: value,
      ...(value === false && { orientationFormRequired: false }),
    }));
  };

  const handleLocationMustChange = (value) => {
    setProjectData((prev) => ({
      ...prev,
      locationMust: value,
      ...(value === false && { proximityToClockInRequired: false }),
    }));
  };

  const handleOpenMapDialog = () => {
    if (projectData.latitude && projectData.longitude) {
      setOpenMapDialog(true);
      return;
    }
    getCoordinatesFromUser()
      .then((coords) => {
        setUserLocation(coords);
        setOpenMapDialog(true);
      })
      .catch((error) => {
        console.error('Error getting user location:', error);
        setOpenMapDialog(true);
      });
  };

  const handleProximityToClockInRadiusChange = (value) => {
    setProjectData((prev) => ({
      ...prev,
      proximityToClockInRadius: value,
      proximityToClockInType: 'radius',
      proximityToClockInArea: [],
      proximityToClockInAreas: [],
    }));
  };

  const handleProximityToClockInAreasChange = (areas) => {
    setProjectData((prev) => ({
      ...prev,
      proximityToClockInAreas: areas,
      // mirror the first area into the legacy field so stale cached
      // bundles still enforce a current polygon
      proximityToClockInArea: areas.length ? areas[0].positions : [],
      proximityToClockInType: 'area',
      proximityToClockInRadius: null,
    }));
  };

  const handleChangeNonEvent = (name, value) => {
    setProjectData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChangeTradeHourlyCost = (code, value) => {
    setProjectData((prev) => {
      const nextCosts = { ...prev.tradeHourlyCosts };
      const trimmed = value?.toString().trim();

      if (!trimmed) {
        delete nextCosts[code];
      } else {
        nextCosts[code] = Number(trimmed);
      }

      return { ...prev, tradeHourlyCosts: nextCosts };
    });

    // Diverging from the parent's inherited values — warn like the assignments section.
    if (tradeRatesInherited) {
      setTradeRatesChangedFromParentWarningOpen(true);
    }
  };

  const handleChangeProjectStreet1 = (place) => {
    setProjectData((prev) => {
      if (!place) {
        return {
          ...prev,
          street1: '',
          street2: '',
          co: '',
          postcode: '',
          city: '',
          country: '',
        };
      }

      return {
        ...prev,
        street1: place.Street
          ? `${place.Street} ${place.AddressNumber || ''}`.trim()
          : prev.street1,
        postcode: place.PostalCode || prev.postcode,
        city: place.Municipality || prev.city,
        country: place.Country === 'FIN' ? 'Suomi' : prev.country,
      };
    });
  };

  const getCoordinatesFromAddress = async () => {
    if (!projectData.street1 && (!projectData.postcode || !projectData.city))
      return;
    setLocationLoading(true);
    try {
      const { latitude, longitude } = await getCoordinatesByAddress({
        street1: projectData.street1,
        street2: projectData.street2,
        postcode: projectData.postcode,
        city: projectData.city,
      });
      if (!latitude || !longitude) {
        console.error('Failed to fetch coordinates');
        return;
      }
      console.log('Fetched coordinates:', { latitude, longitude });
      handleChangeNonEvent('latitude', Number(latitude));
      handleChangeNonEvent('longitude', Number(longitude));
    } catch (error) {
      console.error('Failed to fetch coordinates', error);
    } finally {
      setLocationLoading(false);
    }
  };

  const handleProjectLocationChange = (e) => {
    // Same nameless event as in handleChange, and the reset below has an
    // else branch: without this it would clear locationDescription every
    // time a suggestion is picked.
    if (!e || !e.target || !e.target.name) return;
    handleChange(e);

    // Reset the opposite fields based on which field was changed
    const isLocationDescription =
      e.target.name === 'locationDescription' ||
      e.target.name === 'postcode' ||
      e.target.name === 'city';

    setProjectData((prev) => ({
      ...prev,
      ...(isLocationDescription
        ? {
            street1: '',
            street2: '',
            co: '',
            country: '',
          }
        : {
            locationDescription: '',
          }),
    }));
  };

  const handleChangeContactStreet1 = (place) => {
    setProjectData((prev) => {
      if (!place) {
        return {
          ...prev,
          contact_street1: '',
          contact_street2: '',
          contact_co: '',
          contact_postcode: '',
          contact_city: '',
          contact_country: '',
        };
      }

      return {
        ...prev,
        contact_street1: place.Street
          ? `${place.Street} ${place.AddressNumber || ''}`.trim()
          : prev.contact_street1,
        contact_postcode: place.PostalCode || prev.contact_postcode,
        contact_city: place.Municipality || prev.contact_city,
        contact_country:
          place.Country === 'FIN' ? 'Suomi' : prev.contact_country,
      };
    });
  };

  const handleChangeSiteManagerStreet1 = (place) => {
    setProjectData((prev) => {
      if (!place) {
        return {
          ...prev,
          site_manager_street1: '',
          site_manager_street2: '',
          site_manager_co: '',
          site_manager_postcode: '',
          site_manager_city: '',
          site_manager_country: '',
        };
      }

      return {
        ...prev,
        site_manager_street1: place.Street
          ? `${place.Street} ${place.AddressNumber || ''}`.trim()
          : prev.site_manager_street1,
        site_manager_postcode: place.PostalCode || prev.site_manager_postcode,
        site_manager_city: place.Municipality || prev.site_manager_city,
        site_manager_country:
          place.Country === 'FIN' ? 'Suomi' : prev.site_manager_country,
      };
    });
  };

  const handleChangeContact = (contact) => {
    if (!contact) {
      return;
    }
    setProjectData((prev) => ({
      ...prev,
      contact_first_name: contact.contact_first_name,
      contact_last_name: contact.contact_last_name,
      contact_email: contact.contact_email,
      contact_phone: contact.contact_phone,
    }));
  };

  const handleChangeSiteManager = (siteManager) => {
    if (!siteManager) {
      return;
    }
    setProjectData((prev) => ({
      ...prev,
      site_manager_first_name: siteManager.site_manager_first_name,
      site_manager_last_name: siteManager.site_manager_last_name,
      site_manager_email: siteManager.site_manager_email,
      site_manager_phone: siteManager.site_manager_phone,
      // Identity of the person picked, so the backend can grant them the right
      // to sign the vastaava työnjohtaja slot without guessing from a phone
      // number. Cleared when the fields are typed by hand rather than picked,
      // because then there is no resource to point at.
      site_manager_user_id: siteManager.site_manager_user_id || null,
    }));
  };

  const checkForIlveability = async () => {
    setIlveability(false);
    const ilveability = await getIlveability(parentProject, project.id);
    setIlveability(ilveability);
    if (ilveability === false) {
      // setIlmoitusvelvollinen(false);
      setProjectData((prev) => ({
        ...prev,
        ilve: false,
        ilmoitettava: false,
      }));
    }
  };

  const checkForIntegrationPartner = async (businessId) => {
    const partners = await getIntegrationPartners(businessId);
    console.log('Integration partners:', partners);

    if (
      (partners.includes('Fennoa') ||
        partners.includes('TALENOM') ||
        partners.includes('PROCOUNTOR') ||
        partners.includes('JYDACOM') ||
        partners.includes('TALOSOFTA')) &&
      type === 'create'
    ) {
      setOpenAdditionalSettings(true);
    }
    setIntegrationPartners(partners);
  };

  // Run if project parent changes, check for ilveability and sets a warning if parent empty
  useEffect(() => {
    if (parentProject) {
      setProjectParentWarningOpen(false);
      checkForIlveability();
      checkForIntegrationPartner(businessId);
    } else {
      setProjectParentWarningOpen(true);
    }
  }, [parentProject]);

  useEffect(() => {
    dispatch(loadEmployerAllowancesByBusinessId());

    return () => {
      dispatch(clearAllEmployerAllowanceDrafts());
    };
  }, []);

  const handleAddEmployerAllowanceDraft = () => {
    dispatch(createEmployerAllowanceDraft({}));
  };

  // Gates both turning the proximity geofence on and keeping it on. Disabling a
  // switch does not clear it, so a project can otherwise reach handleSave with
  // the toggle left true and no location to measure against.
  const hasProjectCoordinates = Boolean(
    projectData.latitude && projectData.longitude
  );

  const handleSave = () => {
    const fileName = uuidv4();

    const projectDataToSave = {
      ...projectData,
      ...(parentProject && { parent: parentProject }),
      ...(!companyMode && {
        customer: projectData.customer,
        hasOwnAssignments: projectData.hasOwnAssignments,
        ...(projectData.startAt && {
          startAt: dayjs(projectData.startAt).format('YYYY-MM-DD'),
        }),
        ...(projectData.endAt && {
          endAt: dayjs(projectData.endAt).format('YYYY-MM-DD'),
        }),
      }),

      ...(imageUpdated &&
        projectImage && {
          picture: {
            bucket: Config.S3_BUCKET,
            key: `profile-images/${fileName}`,
            region: Config.AWS_REGION,
          },
        }),
      ...(businessId && { business_id: businessId }),
      ...(!hasProjectCoordinates && { proximityToClockInRequired: false }),
    };

    // Keep inheriting the parent's trade costs until the user actually edits them:
    // don't persist the inherited values as the project's own when unchanged.
    if (
      type === 'edit' &&
      tradeRatesInherited &&
      !tradeRatesChangedFromParentWarningOpen
    ) {
      delete projectDataToSave.tradeHourlyCosts;
    }

    // Filter out null/undefined values
    const filteredProjectData = Object.entries(projectDataToSave).reduce(
      (acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      },
      {}
    );

    const createdFirestopDetails = firestopDetails.filter(
      (firestopDetail) =>
        createdFirestopDetailIds.includes(firestopDetail.id) &&
        !firestopDetail.deletedAt &&
        firestopDetail.name?.toString().trim()
    );
    const updatedFirestopDetails = firestopDetails.filter((firestopDetail) =>
      updatedFirestopDetailIds.includes(firestopDetail.id)
    );

    const createdFirestopProducts = firestopProducts.filter(
      (firestopProduct) =>
        createdFirestopProductIds.includes(firestopProduct.id) &&
        !firestopProduct.deletedAt &&
        firestopProduct.name?.toString().trim()
    );
    const updatedFirestopProducts = firestopProducts.filter((firestopProduct) =>
      updatedFirestopProductIds.includes(firestopProduct.id)
    );

    if (type === 'edit') {
      onUpdate(
        filteredProjectData,
        projectImage,
        fileName,
        imageUpdated,
        assignments,
        createdAssignmentIds,
        updatedAssignmentIds,
        competencyTypes,
        { createdFirestopDetails, updatedFirestopDetails },
        { createdFirestopProducts, updatedFirestopProducts }
      );
    } else {
      onCreate(
        filteredProjectData,
        projectImage,
        fileName,
        imageUpdated,
        assignments,
        createdAssignmentIds,
        competencyTypes,
        { createdFirestopDetails, updatedFirestopDetails },
        { createdFirestopProducts, updatedFirestopProducts }
      );
    }
  };

  const handleDeleteProject = () => {
    if (type === 'edit') {
      onDelete(project);
    }
    setOpenDeleteConfirmationDialog(false);
  };

  const disableProjectLocationDescriptionInput = useMemo(() => {
    return Boolean(
      (projectData.street1 ||
        projectData.street2 ||
        projectData.co ||
        projectData.country) &&
      !projectData.locationDescription
    );
  }, [
    projectData.street1,
    projectData.street2,
    projectData.co,
    projectData.country,
  ]);

  const disableProjectAddressInput = Boolean(projectData.locationDescription);

  const handleChangeProjectImage = (e) => {
    setProjectImage(e.target.files[0]);
    setImageUpdated(true);
    console.log('Project image', e.target.files[0]);
  };

  const handleChangeAssignment = (assignment) => {
    const newAssignments = assignments.map((a) => {
      if (a.id === assignment.id) {
        return assignment;
      }
      return a;
    });
    setAssignments(newAssignments);
    if (
      !updatedAssignmentIds.includes(assignment.id) &&
      !createdAssignmentIds.includes(assignment.id)
    ) {
      setUpdatedAssignmentIds((old) => [...old, assignment.id]);
    }
    setProjectData((prev) => ({
      ...prev,
      hasOwnAssignments: true,
    }));
    setAssignmentCopyInfoOpen(false);
    setAssignmentCopyWarningOpen(true);
  };

  const handleAddForeman = () => {
    const id = uuidv4();
    setProjectData((prev) => ({
      ...prev,
      foremanList: [
        ...prev.foremanList,
        {
          id: id,
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          street1: '',
          street2: '',
          co: '',
          postcode: '',
          city: '',
          country: '',
        },
      ],
    }));

    setOpenForemanListInformation((prevState) => ({
      ...prevState,
      [id]: true,
    }));
  };

  const handleChangeForeman = (e, id) => {
    if (!e) return;
    const { name, value } = e.target;

    setProjectData((prev) => ({
      ...prev,
      foremanList: prev.foremanList.map((foreman) => {
        if (foreman.id === id) {
          return {
            ...foreman,
            [name]: value,
          };
        }
        return foreman;
      }),
    }));
  };

  const handleDeleteForeman = (id) => {
    setProjectData((prev) => ({
      ...prev,
      foremanList: prev.foremanList.filter((foreman) => foreman.id !== id),
    }));
  };

  const handleChangeValueOfAssignmentAndAllChildren = (
    assignment,
    name,
    value
  ) => {
    const findAllChildren = (assignmentId, allAssignments) => {
      const children = allAssignments.filter((a) => a.parent === assignmentId);
      let allChildren = [...children];

      children.forEach((child) => {
        allChildren = [
          ...allChildren,
          ...findAllChildren(child.id, allAssignments),
        ];
      });

      return allChildren;
    };

    const allChildren = findAllChildren(assignment.id, assignments);
    const affectedAssignmentIds = [
      assignment.id,
      ...allChildren.map((a) => a.id),
    ];

    const newAssignments = assignments.map((a) => {
      if (affectedAssignmentIds.includes(a.id)) {
        return { ...a, [name]: value };
      }
      return a;
    });

    setAssignments(newAssignments);

    // Update tracking arrays for assignments that were modified
    affectedAssignmentIds.forEach((id) => {
      if (
        !updatedAssignmentIds.includes(id) &&
        !createdAssignmentIds.includes(id)
      ) {
        setUpdatedAssignmentIds((old) => [...old, id]);
      }
    });

    setProjectData((prev) => ({
      ...prev,
      hasOwnAssignments: true,
    }));
    setAssignmentCopyInfoOpen(false);
    setAssignmentCopyWarningOpen(true);
  };

  const handleAddAssignment = (parent) => {
    const id = uuidv4();
    const number = !parent
      ? assignments.filter((a) => !a.parent && !a.deletedAt).length + 1
      : assignments.filter((a) => a.parent === parent && !a.deletedAt).length +
        1;

    setAssignments([
      ...assignments,
      {
        id: id,
        name: '',
        number: number,
        unit: '',
        unitPrice: '',
        unitAmountGoal: '',
        ...(parent && { parent }),
      },
    ]);

    setOpenAssignmentsArray([...openAssignmentsArray, id]);
    if (!createdAssignmentIds.includes(id)) {
      setCreatedAssignmentIds((old) => [...old, id]);
    }
    setProjectData((prev) => ({
      ...prev,
      hasOwnAssignments: true,
    }));
    setAssignmentCopyInfoOpen(false);
    setAssignmentCopyWarningOpen(true);
  };

  const handleDeleteAssignment = (assignment) => {
    // If assignment is new, remove it from assignments, else mark it as deleted
    const isNew = createdAssignmentIds.includes(assignment.id);
    if (isNew) {
      setAssignments((old) => old.filter((a) => a.id !== assignment.id));
      setCreatedAssignmentIds((old) =>
        old.filter((id) => id !== assignment.id)
      );
    } else {
      setAssignments((old) => [
        ...old.filter((a) => a.id !== assignment.id),
        {
          ...assignment,
          deletedAt: new Date().toISOString(),
        },
      ]);
      if (!updatedAssignmentIds.includes(assignment.id)) {
        setUpdatedAssignmentIds((old) => [...old, assignment.id]);
      }
    }

    // Remove all children too recursively
    const children = assignments.filter((a) => a.parent === assignment.id);
    if (children.length) {
      children.forEach((child) => handleDeleteAssignment(child));
    }
    setProjectData((prev) => ({
      ...prev,
      hasOwnAssignments: true,
    }));
    setAssignmentCopyInfoOpen(false);
    setAssignmentCopyWarningOpen(true);
  };

  const handleDeleteAllAssignments = () => {
    // If assignment is new, remove it from assignments, else mark it as deleted
    const newAssignments = assignments
      .map((a) => ({
        ...a,
        deletedAt: new Date().toISOString(),
      }))
      .filter((a) => !createdAssignmentIds.includes(a.id));

    setAssignments(newAssignments); // Mark all updated assignments as deleted, new assignments were removed

    setCreatedAssignmentIds([]); // Reset state of created assignments

    setUpdatedAssignmentIds(newAssignments.map((a) => a.id)); // Mark all assignments as updated

    setOpenAssignmentsArray([]); // Reset state of open assignments
    setProjectData((prev) => ({
      ...prev,
      hasOwnAssignments: true,
    }));
    setAssignmentCopyInfoOpen(false); // Close info about copied assignments
    setAssignmentCopyWarningOpen(true);
  };

  const handleToggleRequiredCompetencyCardType = (value, checked) => {
    setProjectData((prev) => {
      const current = prev.requiredCompetencyCardTypes || [];
      return {
        ...prev,
        requiredCompetencyCardTypes: checked
          ? Array.from(new Set([...current, value]))
          : current.filter((entry) => entry !== value),
      };
    });
  };

  useEffect(() => {
    if (type !== 'edit' || !project.id) {
      return;
    }

    let isCancelled = false;

    const loadFirestopDetailAndProductItems = async () => {
      setIsLoadingFirestopDetails(true);
      setIsLoadingFirestopProducts(true);
      try {
        const {
          firestopDetails: loadedFirestopDetails,
          firestopProducts: loadedFirestopProducts,
        } = await getProjectFirestopDetailAndProductItems(project.id);

        if (!isCancelled) {
          setFirestopDetails(loadedFirestopDetails || []);
          setCreatedFirestopDetailIds([]);
          setUpdatedFirestopDetailIds([]);

          setFirestopProducts(loadedFirestopProducts || []);
          setCreatedFirestopProductIds([]);
          setUpdatedFirestopProductIds([]);
        }
      } catch (error) {
        console.error(
          'Error loading firestop detail and product items:',
          error
        );
      } finally {
        if (!isCancelled) {
          setIsLoadingFirestopDetails(false);
          setIsLoadingFirestopProducts(false);
        }
      }
    };

    loadFirestopDetailAndProductItems();

    return () => {
      isCancelled = true;
    };
  }, [type, project.id]);

  const handleAddFirestopDetail = () => {
    const createdFirestopDetailId = uuidv4();

    setFirestopDetails((currentFirestopDetails) => [
      ...currentFirestopDetails,
      {
        id: createdFirestopDetailId,
        name: '',
      },
    ]);

    setCreatedFirestopDetailIds((currentCreatedFirestopDetailIds) => [
      ...currentCreatedFirestopDetailIds,
      createdFirestopDetailId,
    ]);

    return createdFirestopDetailId;
  };

  const handleChangeFirestopDetail = (firestopDetailId, name) => {
    setFirestopDetails((currentFirestopDetails) =>
      currentFirestopDetails.map((firestopDetail) => {
        if (firestopDetail.id !== firestopDetailId) {
          return firestopDetail;
        }

        return { ...firestopDetail, name };
      })
    );

    if (
      !createdFirestopDetailIds.includes(firestopDetailId) &&
      !updatedFirestopDetailIds.includes(firestopDetailId)
    ) {
      setUpdatedFirestopDetailIds((currentUpdatedFirestopDetailIds) => [
        ...currentUpdatedFirestopDetailIds,
        firestopDetailId,
      ]);
    }
  };

  const handleDeleteFirestopDetail = (firestopDetailId) => {
    const isCreatedFirestopDetail =
      createdFirestopDetailIds.includes(firestopDetailId);

    if (isCreatedFirestopDetail) {
      setFirestopDetails((currentFirestopDetails) =>
        currentFirestopDetails.filter(
          (firestopDetail) => firestopDetail.id !== firestopDetailId
        )
      );
      setCreatedFirestopDetailIds((currentCreatedFirestopDetailIds) =>
        currentCreatedFirestopDetailIds.filter((id) => id !== firestopDetailId)
      );
      return;
    }

    setFirestopDetails((currentFirestopDetails) =>
      currentFirestopDetails.map((firestopDetail) => {
        if (firestopDetail.id !== firestopDetailId) {
          return firestopDetail;
        }

        return { ...firestopDetail, deletedAt: new Date().toISOString() };
      })
    );

    if (!updatedFirestopDetailIds.includes(firestopDetailId)) {
      setUpdatedFirestopDetailIds((currentUpdatedFirestopDetailIds) => [
        ...currentUpdatedFirestopDetailIds,
        firestopDetailId,
      ]);
    }
  };

  const handleAddFirestopProduct = () => {
    const createdFirestopProductId = uuidv4();

    setFirestopProducts((currentFirestopProducts) => [
      ...currentFirestopProducts,
      {
        id: createdFirestopProductId,
        name: '',
      },
    ]);

    setCreatedFirestopProductIds((currentCreatedFirestopProductIds) => [
      ...currentCreatedFirestopProductIds,
      createdFirestopProductId,
    ]);

    return createdFirestopProductId;
  };

  const handleChangeFirestopProduct = (firestopProductId, name) => {
    setFirestopProducts((currentFirestopProducts) =>
      currentFirestopProducts.map((firestopProduct) => {
        if (firestopProduct.id !== firestopProductId) {
          return firestopProduct;
        }

        return { ...firestopProduct, name };
      })
    );

    if (
      !createdFirestopProductIds.includes(firestopProductId) &&
      !updatedFirestopProductIds.includes(firestopProductId)
    ) {
      setUpdatedFirestopProductIds((currentUpdatedFirestopProductIds) => [
        ...currentUpdatedFirestopProductIds,
        firestopProductId,
      ]);
    }
  };

  const handleDeleteFirestopProduct = (firestopProductId) => {
    const isCreatedFirestopProduct =
      createdFirestopProductIds.includes(firestopProductId);

    if (isCreatedFirestopProduct) {
      setFirestopProducts((currentFirestopProducts) =>
        currentFirestopProducts.filter(
          (firestopProduct) => firestopProduct.id !== firestopProductId
        )
      );
      setCreatedFirestopProductIds((currentCreatedFirestopProductIds) =>
        currentCreatedFirestopProductIds.filter(
          (id) => id !== firestopProductId
        )
      );
      return;
    }

    setFirestopProducts((currentFirestopProducts) =>
      currentFirestopProducts.map((firestopProduct) => {
        if (firestopProduct.id !== firestopProductId) {
          return firestopProduct;
        }

        return { ...firestopProduct, deletedAt: new Date().toISOString() };
      })
    );

    if (!updatedFirestopProductIds.includes(firestopProductId)) {
      setUpdatedFirestopProductIds((currentUpdatedFirestopProductIds) => [
        ...currentUpdatedFirestopProductIds,
        firestopProductId,
      ]);
    }
  };

  const handleAddCompetencyType = () => {
    const createdCompetencyTypeId = uuidv4();

    setCompetencyTypes((oldCompetencyTypes) => [
      ...oldCompetencyTypes,
      {
        id: createdCompetencyTypeId,
        name: '',
        deletedAt: null,
        isNew: true,
      },
    ]);
    setHasCompetencyTypeEdits(true);
    return createdCompetencyTypeId;
  };

  const handleChangeCompetencyType = (competencyTypeId, name) => {
    setCompetencyTypes((oldCompetencyTypes) =>
      oldCompetencyTypes.map((competencyType) => {
        if (competencyType.id !== competencyTypeId) {
          return competencyType;
        }

        return {
          ...competencyType,
          name,
        };
      })
    );
    setHasCompetencyTypeEdits(true);
  };

  const handleDeleteCompetencyType = (competencyTypeId) => {
    setCompetencyTypes((oldCompetencyTypes) =>
      oldCompetencyTypes
        .map((competencyType) => {
          if (competencyType.id !== competencyTypeId) {
            return competencyType;
          }

          if (competencyType.isNew) {
            return null;
          }

          return {
            ...competencyType,
            deletedAt: new Date().toISOString(),
          };
        })
        .filter(Boolean)
    );
    setHasCompetencyTypeEdits(true);
  };

  const handleReadXLS = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }
    // Get new assignments from XLS
    const excelAssignments = await readAssignmentsFromXLS(file);

    // Remove all previous assignments, by marking old ones as deleted, and removing new ones completely
    const newAssignments = assignments
      .map((a) => ({
        ...a,
        deletedAt: new Date().toISOString(),
      }))
      .filter((a) => !createdAssignmentIds.includes(a.id));

    setUpdatedAssignmentIds(newAssignments.map((a) => a.id)); // Mark all removed assignments as updated

    setOpenAssignmentsArray([]); // Reset state of open assignments

    // Add removed assignments to the list, and all new assignments from XLS
    setAssignments([...newAssignments, ...excelAssignments]);
    setCreatedAssignmentIds(excelAssignments.map((a) => a.id)); // Reset state of created assignments
    setProjectData((prev) => ({
      ...prev,
      hasOwnAssignments: true,
    }));
    setAssignmentCopyInfoOpen(false);
    setAssignmentCopyWarningOpen(true);
  };

  const handleWriteXLS = async () => {
    await writeAssignmentsToXLS(activeAssignments, projectData.name);
  };

  const handleDownloadXLSBase = () => {
    const link = document.createElement('a');
    link.download = `L5_litterapohja.xlsx`;
    link.href = process.env.PUBLIC_URL + '/L5_litterapohja.xlsx';
    link.click();
  };

  const { requiredFields, isSaveDisabled, hasValidAssignments } =
    useMemo(() => {
      const requiredFields = { name: true };

      switch (projectData.ilmoitettava) {
        case true:
          if (projectData.locationDescription) {
            requiredFields.locationDescription = true;
            requiredFields.postcode = true;
            requiredFields.city = true;
          } else {
            requiredFields.street1 = true;
            requiredFields.postcode = true;
            requiredFields.city = true;
          }
          requiredFields.contact_first_name = true;
          requiredFields.contact_last_name = true;
          requiredFields.contact_phone = true;
          requiredFields.endAt = true;
          requiredFields.startAt = true;
          break;
        default:
          break;
      }

      switch (projectData.proximityToClockInRequired) {
        case true:
          if (projectData.locationDescription) {
            requiredFields.locationDescription = true;
          } else {
            requiredFields.street1 = true;
            requiredFields.postcode = true;
            requiredFields.city = true;
          }
          break;
        default:
          break;
      }

      const hasRequiredFields = Object.entries(requiredFields).every(
        ([key]) => {
          const value = projectData[key]?.toString().trim();
          if (key === 'name') return (value || '').length >= 3;
          return !!value;
        }
      );

      const hasValidAssignments =
        assignments.length === 0 ||
        assignments.every((assignment) => assignment.number && assignment.name);

      const hasValidCompetencyTypes = activeCompetencyTypes.every(
        (competencyType) => {
          if (!competencyType.isNew) {
            return true;
          }

          const normalizedName =
            competencyType.name?.toString().trim().toLowerCase() || '';

          if (!normalizedName) {
            return false;
          }

          return (
            activeCompetencyTypes.filter((otherCompetencyType) => {
              const otherNormalizedName =
                otherCompetencyType.name?.toString().trim().toLowerCase() || '';

              return otherNormalizedName === normalizedName;
            }).length === 1
          );
        }
      );

      const isSaveDisabled =
        !hasRequiredFields || !hasValidAssignments || !hasValidCompetencyTypes;

      return { requiredFields, isSaveDisabled, hasValidAssignments };
    }, [activeCompetencyTypes, projectData]);

  const isRequired = (name) => requiredFields[name] || false;

  const isHighestNormalProject = project.parent === project.masterParent;

  const showCustomerTextInsteadOfProject =
    isHighestNormalProject && project.business_id === '0871376-9';

  return (
    <Dialog
      aria-labelledby="project-dialog"
      classes={{ paper: classes.dialogPaper }}
      disableBackdropClick
      fullScreen={!upSm}
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
    >
      {openColorPickerDialog && (
        <ColorPickerDialog
          handleColorChange={(value) =>
            handleChangeNonEvent('mainColor', value?.hex || value)
          }
          open={openColorPickerDialog}
          onClose={() => setOpenColorPickerDialog(false)}
          mainColor={projectData.mainColor}
        />
      )}
      {openMapDialog && (
        <MapDialog
          open={openMapDialog}
          onClose={() => setOpenMapDialog(false)}
          selectPositionMode
          handleSelectPosition={(position) => {
            handleChangeNonEvent('latitude', position.latitude);
            handleChangeNonEvent('longitude', position.longitude);
          }}
          defaultSelectedPosition={{
            latitude:
              projectData.latitude ||
              userLocation.latitude ||
              60.16955736341621,
            longitude:
              projectData.longitude ||
              userLocation.longitude ||
              24.93818796706058,
          }}
          showRadiusOnSelectedPosition={
            projectData.proximityToClockInType === 'radius'
          }
          selectedPositionRadius={projectData.proximityToClockInRadius || 100}
        />
      )}

      {openDrawCircleMapDialog && (
        <MapDialog
          open={openDrawCircleMapDialog}
          onClose={() => setOpenDrawCircleMapDialog(false)}
          selectPositionMode
          handleSelectPosition={(position) => {
            handleChangeNonEvent('latitude', position.latitude);
            handleChangeNonEvent('longitude', position.longitude);
          }}
          defaultSelectedPosition={{
            latitude: projectData.latitude,
            longitude: projectData.longitude,
          }}
          drawCircleMode
          handleCircleRadius={(radius) => {
            handleProximityToClockInRadiusChange(radius);
          }}
          defaultRadius={projectData.proximityToClockInRadius || 100}
        />
      )}

      {openDrawAreaMapDialog && (
        <MapDialog
          open={openDrawAreaMapDialog}
          onClose={() => setOpenDrawAreaMapDialog(false)}
          drawAreaMode
          defaultSelectedPosition={{
            latitude: projectData.latitude,
            longitude: projectData.longitude,
          }}
          handleDrawAreas={(areas) => {
            handleProximityToClockInAreasChange(areas);
          }}
          defaultAreas={projectData.proximityToClockInAreas}
        />
      )}
      {openDeleteConfirmationDialog && (
        <ProjectDeleteConfirmationDialog
          open={openDeleteConfirmationDialog}
          onClose={() => setOpenDeleteConfirmationDialog(false)}
          project={project}
          handleDelete={handleDeleteProject}
        />
      )}
      <DialogHeader
        icon={
          type === 'create' ? (
            <Icon path={mdiPlus} size={1} className={classes.icon} />
          ) : (
            <Icon path={mdiPencil} size={1} className={classes.icon} />
          )
        }
        onClose={onClose}
        title={
          type === 'create'
            ? showCustomerTextInsteadOfProject
              ? t('project_dialog.title_create_customer')
              : t('project_dialog.title_create_project')
            : showCustomerTextInsteadOfProject
              ? t('project_dialog.title_edit_customer')
              : t('project_dialog.title_edit_project')
        }
      />

      <DialogContent className={classes.dialogContent}>
        <ActionContainer // Projektin tiedot
          title={t('project_dialog.section_project_information')}
          required
        >
          <Grid container spacing={1} item>
            <Grid container item xs={2}>
              <ProjectImage
                fileObject={projectImage}
                imageUpdated={imageUpdated}
              />
            </Grid>
            <Grid item xs={10} container spacing={1}>
              <Grid item xs={12}>
                <TextInput
                  label={t('project_dialog.field_project_name')}
                  fullWidth
                  name="name"
                  value={projectData.name}
                  onChange={handleChange}
                  helperText={t('project_dialog.help_minimum_3_characters')}
                  required={isRequired('name')}
                  disabled={loading}
                />
              </Grid>
              <Grid container justifyContent="space-between" spacing={1} item>
                <Grid item>
                  <Grid container spacing={1}>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="primary"
                        component="label"
                        disabled={loading}
                      >
                        {projectImage
                          ? t('project_dialog.button_change_image')
                          : t('project_dialog.button_add_image')}
                        <input
                          type="file"
                          accept="image/png, image/jpeg"
                          hidden
                          onChange={handleChangeProjectImage}
                        />
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="danger"
                        component="label"
                        disabled={!projectImage || loading}
                        onClick={() => setProjectImage(null)}
                      >
                        {t('project_dialog.button_remove_image')}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>

                {companyMode && (
                  <Grid item>
                    <Button
                      color="primary"
                      onClick={() => setOpenColorPickerDialog(true)}
                    >
                      <Icon
                        path={mdiSquare}
                        size={1}
                        color={projectData.mainColor || 'black'}
                      />
                      {t('project_dialog.button_choose_main_color')}
                    </Button>
                  </Grid>
                )}
              </Grid>
              {companyMode && projectImage && (
                <Grid item xs={12}>
                  <SwitchItem
                    title={t('project_dialog.toggle_circular_logo')}
                    checked={projectData.logoCircular}
                    onChange={(checked) =>
                      handleChangeNonEvent('logoCircular', checked)
                    }
                    disabled={loading}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
          {!companyMode && (
            <>
              <Grid item xs={12} style={{ margin: theme.spacing(1) }}></Grid>
              <Grid item xs={12}>
                {projectParentWarningOpen && type === 'create' && (
                  <Alert
                    severity="warning"
                    variant="outlined"
                    onClose={() => setProjectParentWarningOpen(false)}
                  >
                    <AlertTitle>
                      {t('project_dialog.alert_warning_title')}
                    </AlertTitle>
                    {t('project_dialog.alert_project_outside_company')}
                  </Alert>
                )}
              </Grid>

              {showProjectStatusField && (
                <Grid item xs={12}>
                  <ProjectStatusSelect
                    value={projectData.status}
                    onChange={handleProjectStatusChange}
                    dialogType={type}
                    projectType={project.type}
                    isPermissionToEditStatus={isPermissionToEditProjectStatus}
                    loading={loading}
                  />
                </Grid>
              )}
              {!companyMode && (
                <>
                  <SwitchItem
                    title={t('project_dialog.toggle_reportable')}
                    checked={projectData.ilmoitettava}
                    onChange={(checked) =>
                      handleIlmoitusvelvollinenChange(checked)
                    }
                    disabled={
                      loading || !ilveability || !isReportableAccessible
                    }
                  />

                  <Grid
                    item
                    xs={12}
                    style={{ margin: theme.spacing(1) }}
                  ></Grid>
                </>
              )}

              <Grid item xs={12}>
                <ComboBox
                  items={projects}
                  selected={parentProject}
                  onChange={setParentProject}
                  label={t('project_dialog.field_host_project')}
                  emptyAsNull
                  disabled={type !== 'create' || companyMode} // Change later
                />
              </Grid>

              <Grid container item spacing={1}>
                <Grid item xs={6}>
                  <DateTimePicker
                    type="date"
                    name="startAt"
                    value={projectData.startAt}
                    onChange={(value) => handleChangeNonEvent('startAt', value)}
                    viewmode="days"
                    inputProps={{
                      label: t('calendar.start_date'),
                    }}
                    required={isRequired('startAt')}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={6}>
                  <DateTimePicker
                    type="date"
                    name="endAt"
                    value={projectData.endAt}
                    onChange={(value) => handleChangeNonEvent('endAt', value)}
                    viewmode="days"
                    inputProps={{
                      label: t('calendar.end_date'),
                    }}
                    required={isRequired('endAt')}
                    disabled={loading}
                    error={
                      !!projectData.endAt &&
                      dayjs(projectData.endAt).isBefore(dayjs(), 'day')
                    }
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} style={{ margin: theme.spacing(1) }}></Grid>
              <Grid item xs={12} sm={12}>
                <TextInput
                  label={t('project_dialog.field_short_description')}
                  name="shortDescription"
                  fullWidth
                  value={projectData.shortDescription}
                  onChange={handleChange}
                  inputProps={{ maxLength: 30 }}
                  required={isRequired('shortDescription')}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <TextInput
                  label={t('project_dialog.field_description')}
                  name="longDescription"
                  fullWidth
                  value={projectData.longDescription}
                  onChange={handleChange}
                  multiline
                  required={isRequired('longDescription')}
                  minRows={3}
                  disabled={loading}
                />
              </Grid>
            </>
          )}
        </ActionContainer>
        <ActionContainer // Projektin asetukset
          title={t('project_dialog.section_project_settings')}
          closable
          open={openProjectSettings}
          setOpen={setOpenProjectSettings}
        >
          {companyMode && (
            <SwitchItem
              title={t('project_dialog.toggle_prevent_simultaneous_clock_ins')}
              checked={projectData.restrictEmployeesMultipleClockIn}
              onChange={(checked) =>
                handleChangeNonEvent(
                  'restrictEmployeesMultipleClockIn',
                  checked
                )
              }
              disabled={loading}
            />
          )}
          <SwitchItem
            title={t('project_dialog.toggle_assignment_required_on_clock_out')}
            name="assignmentRequired"
            checked={projectData.assignmentRequired}
            onChange={(checked) =>
              handleChangeNonEvent('assignmentRequired', checked)
            }
            disabled={loading}
          />
          <SwitchItem
            title={t('project_dialog.toggle_prevent_clock_in_after_end_date')}
            name="preventClockInAfterEndDate"
            checked={projectData.preventClockInAfterEndDate}
            onChange={(checked) =>
              handleChangeNonEvent('preventClockInAfterEndDate', checked)
            }
            disabled={loading}
          />
          <SwitchItem
            title={t('project_dialog.toggle_profile_image_required')}
            name="pictureMust"
            checked={projectData.pictureMust}
            onChange={(checked) => handleChangeNonEvent('pictureMust', checked)}
            disabled={loading}
          />
          <SwitchItem
            title={t(
              'project_dialog.toggle_valid_tax_number_required_on_clock_in'
            )}
            name="validTaxNumberRequired"
            checked={projectData.validTaxNumberRequired}
            onChange={(checked) =>
              handleChangeNonEvent('validTaxNumberRequired', checked)
            }
            disabled={loading}
          />
          <Grid item xs={12} style={{ margin: theme.spacing(1) }}></Grid>
          <SwitchItem
            title={t('project_dialog.toggle_online_orientation_required')}
            name="orientationRequired"
            checked={projectData.orientationRequired}
            onChange={(checked) => handleOrientationRequiredChange(checked)}
            disabled={loading}
          />
          <SwitchItem
            title={t('project_dialog.toggle_show_orientation_form_on_login')}
            name="autoShowOrientationForm"
            checked={projectData.autoShowOrientationForm}
            onChange={(checked) => handleAutoShowOrientationFormChange(checked)}
            disabled={loading}
          >
            <Grid container item spacing={1}>
              <Grid
                item
                xs={1}
                container
                justifyContent="center"
                alignItems="center"
                style={{ display: 'flex' }}
              >
                <Icon path={mdiArrowRightBottom} size={1} />
              </Grid>
              <Grid item xs={11}>
                <SwitchItem
                  title={t(
                    'project_dialog.toggle_orientation_form_required_on_clock_in'
                  )}
                  name="orientationFormRequired"
                  checked={projectData.orientationFormRequired}
                  onChange={(checked) =>
                    handleChangeNonEvent('orientationFormRequired', checked)
                  }
                  disabled={loading}
                />
              </Grid>
            </Grid>
          </SwitchItem>

          {!companyMode && (
            <>
              <Grid item xs={12} style={{ margin: theme.spacing(1) }}></Grid>
              <SwitchItem
                title={t('project_dialog.toggle_automatic_clock_out')}
                name="autoClockOut"
                checked={projectData.autoClockOut}
                onChange={(checked) =>
                  handleChangeNonEvent('autoClockOut', checked)
                }
                disabled={loading}
              >
                <Grid container item spacing={1}>
                  <Grid
                    item
                    xs={1}
                    container
                    justifyContent="center"
                    alignItems="center"
                    style={{ display: 'flex' }}
                  >
                    <Icon path={mdiArrowRightBottom} size={1} />
                  </Grid>
                  <Grid container item spacing={1} xs={11}>
                    <Grid
                      item
                      sm={12}
                      md={5}
                      container
                      justifyContent="flex-end"
                      alignItems="center"
                    >
                      <TextInput
                        label={t('project_dialog.field_maximum_duration')}
                        fullWidth
                        value={projectData.autoClockOutThreshold}
                        name="autoClockOutThreshold"
                        onChange={handleChange}
                        disabled={loading}
                        select
                      >
                        {autoClockOutOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextInput>
                    </Grid>
                    <Grid
                      item
                      sm={12}
                      md={7}
                      container
                      justifyContent="flex-end"
                      alignItems="center"
                    >
                      <TextInput
                        label={t(
                          'project_dialog.field_automatic_clock_out_duration'
                        )}
                        fullWidth
                        value={projectData.autoClockOutTrim}
                        name="autoClockOutTrim"
                        onChange={handleChange}
                        disabled={loading}
                        select
                      >
                        {autoClockOutOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextInput>
                    </Grid>
                  </Grid>
                </Grid>
              </SwitchItem>
            </>
          )}
          <Grid item xs={12} style={{ margin: theme.spacing(1) }}></Grid>
          <SwitchItem
            title={t(
              'project_dialog.toggle_project_bank_structure_copied_for_subprojects'
            )}
            name="isProjectBankFolderStructureCopied"
            checked={projectData.isProjectBankFolderStructureCopied}
            onChange={(checked) =>
              handleChangeNonEvent(
                'isProjectBankFolderStructureCopied',
                checked
              )
            }
            disabled={loading}
          />
          <Grid item xs={12} style={{ margin: theme.spacing(1) }}></Grid>
          <SwitchItem
            title={t(
              'project_dialog.toggle_tilaajavastuu_documents_fetched_by_default'
            )}
            name="isTilaajavastuuDocumentsFetchedByDefault"
            checked={projectData.isTilaajavastuuDocumentsFetchedByDefault}
            onChange={(checked) =>
              handleChangeNonEvent(
                'isTilaajavastuuDocumentsFetchedByDefault',
                checked
              )
            }
            disabled={loading}
          />
          <Grid item xs={12} style={{ margin: theme.spacing(1) }}></Grid>

          <Grid
            item
            xs={12}
            container
            justifyContent="flex-end"
            alignItems="center"
          >
            <SwitchItem
              title={t(
                'project_dialog.toggle_prevent_clock_in_without_approved_organization'
              )}
              name={'isOrganizationApprovalRequiredForClockIn'}
              checked={projectData.isOrganizationApprovalRequiredForClockIn}
              onChange={(checked) =>
                handleChangeNonEvent(
                  'isOrganizationApprovalRequiredForClockIn',
                  checked
                )
              }
              disabled={loading}
            ></SwitchItem>
          </Grid>

          <Grid
            item
            xs={12}
            container
            justifyContent="flex-end"
            alignItems="center"
          >
            <SwitchItem
              title={t(
                'project_dialog.toggle_prevent_subcontractors_own_employee_records_editing'
              )}
              name={'isSubcontractorsOwnEmployeeRecordEditingRestricted'}
              checked={
                projectData.isSubcontractorsOwnEmployeeRecordEditingRestricted
              }
              onChange={(checked) =>
                handleChangeNonEvent(
                  'isSubcontractorsOwnEmployeeRecordEditingRestricted',
                  checked
                )
              }
              disabled={loading}
            ></SwitchItem>
          </Grid>

          {companyMode ? (
            <Grid item xs={12} container alignItems="center" spacing={1}>
              <Grid item xs={9}>
                <Typography>
                  {t('project_dialog.automatic_meal_break_reduction_length')}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <TextInput
                  fullWidth
                  value={projectData.automaticMealBreakReductionLengthMinutes}
                  name="automaticMealBreakReductionLengthMinutes"
                  onChange={handleChange}
                  disabled={loading}
                  select
                >
                  {mealBreakLengthMinutesOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextInput>
              </Grid>
            </Grid>
          ) : null}

          {/* <SwitchItem ENABLE WHEN IMAGE REQUIREMENTS ARE IMPLEMENTED
            title="Kuva on pakollinen leimauksessa"
            checked={clockImageRequired}
            onChange={(checked) => handleChangeImageRequired(checked)}
            disabled={loading}
          >
            <Grid
              container
              item
              spacing={1}
              // style={{ backgroundColor: theme.palette.primary.main, borderRadius: theme.spacing(1)}}
            >
              <Grid
                item
                xs={6}
                container
                justifyContent="flex-end"
                alignItems="center"
              >
                <TextInput
                  label="Sisäänleimauksessa"
                  fullWidth
                  value={clockInImageAmount}
                  onChange={(e) => setClockInImageAmount(e.target.value)}
                  disabled={loading}
                  select
                >
                  <MenuItem value={0}>{'Ei pakollinen'}</MenuItem>
                  <MenuItem value={1}>{'1 kuva'}</MenuItem>
                  <MenuItem value={2}>{'2 kuvaa'}</MenuItem>
                  <MenuItem value={3}>{'3 kuvaa'}</MenuItem>
                  <MenuItem value={4}>{'4 kuvaa'}</MenuItem>
                </TextInput>
              </Grid>
              <Grid
                item
                xs={6}
                container
                justifyContent="flex-end"
                alignItems="center"
              >
                <TextInput
                  label="Ulosleimauksessa"
                  fullWidth
                  value={clockOutImageAmount}
                  onChange={(e) => setClockOutImageAmount(e.target.value)}
                  disabled={loading}
                  select
                >
                  <MenuItem value={0}>{'Ei pakollinen'}</MenuItem>
                  <MenuItem value={1}>{'1 kuva'}</MenuItem>
                  <MenuItem value={2}>{'2 kuvaa'}</MenuItem>
                  <MenuItem value={3}>{'3 kuvaa'}</MenuItem>
                  <MenuItem value={4}>{'4 kuvaa'}</MenuItem>
                </TextInput>
              </Grid>
            </Grid>
          </SwitchItem> */}
          <Grid item xs={12} style={{ margin: theme.spacing(1) }}></Grid>
          <SwitchItem
            title={t('project_dialog.toggle_competency_cards_required')}
            name="competenciesRequired"
            checked={projectData.competenciesRequired}
            onChange={(checked) =>
              handleChangeNonEvent('competenciesRequired', checked)
            }
            disabled={loading}
          >
            <RequiredCompetencyCardsContent
              loading={loading}
              isLoadingCompetencyTypes={props.isLoadingBusinessCompetencyTypes}
              competencyTypes={competencyTypes}
              selectedRequiredCardTypes={
                projectData.requiredCompetencyCardTypes
              }
              onToggleRequiredCardType={handleToggleRequiredCompetencyCardType}
            />
          </SwitchItem>
        </ActionContainer>
        <ActionContainer // Projektin osoite
          title={t('project_dialog.section_project_address')}
          closable
          open={openProjectAddress}
          setOpen={setOpenProjectAddress}
          required={projectData.ilmoitettava}
        >
          <Grid container item spacing={1}>
            <Grid item xs={12} sm={6}>
              <AddressAutocomplete
                label={t('project_dialog.field_street_address')}
                name="street1"
                value={projectData.street1}
                onChange={handleProjectLocationChange}
                handleSelect={handleChangeProjectStreet1}
                required={isRequired('street1')}
                disabled={loading || disableProjectAddressInput}
                onBlur={() => {
                  getCoordinatesFromAddress();
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextInput
                label={t('project_dialog.field_street_address_2')}
                name="street2"
                fullWidth
                value={projectData.street2}
                onChange={handleProjectLocationChange}
                disabled={loading || disableProjectAddressInput}
              />
            </Grid>
            <Grid item xs={6}>
              <TextInput
                label="C/O"
                name="co"
                fullWidth
                value={projectData.co}
                onChange={handleProjectLocationChange}
                disabled={loading || disableProjectAddressInput}
              />
            </Grid>
            <Grid item xs={6}>
              <TextInput
                label={t('project_dialog.field_postal_code')}
                name="postcode"
                fullWidth
                value={projectData.postcode}
                onChange={handleProjectLocationChange}
                required={isRequired('postcode')}
                disabled={loading || disableProjectAddressInput}
                onBlur={() => {
                  getCoordinatesFromAddress();
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextInput
                label={t('project_dialog.field_city')}
                name="city"
                fullWidth
                value={projectData.city}
                onChange={handleProjectLocationChange}
                required={isRequired('city')}
                disabled={loading || disableProjectAddressInput}
                onBlur={() => {
                  getCoordinatesFromAddress();
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextInput
                label={t('project_dialog.field_country')}
                name="country"
                fullWidth
                value={projectData.country}
                onChange={handleProjectLocationChange}
                disabled={loading || disableProjectAddressInput}
              />
            </Grid>

            <Grid
              item
              container
              xs={12}
              sm={12}
              justifyContent="center"
              alignItems="center"
            >
              <Grid>
                <Typography>{t('project_dialog.text_or')}</Typography>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={12}>
              <TextInput
                label={t('project_dialog.field_location_description')}
                name="locationDescription"
                fullWidth
                value={projectData.locationDescription}
                onChange={handleProjectLocationChange}
                required={isRequired('locationDescription')}
                disabled={loading || disableProjectLocationDescriptionInput}
              />
            </Grid>
            <Grid item xs={6}>
              <TextInput
                label={t('project_dialog.field_postal_code')}
                name="postcode"
                fullWidth
                value={projectData.postcode}
                onChange={handleProjectLocationChange}
                required={isRequired('postcode')}
                disabled={loading || disableProjectLocationDescriptionInput}
                onBlur={() => {
                  getCoordinatesFromAddress();
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextInput
                label={t('project_dialog.field_city')}
                name="city"
                fullWidth
                value={projectData.city}
                onChange={handleProjectLocationChange}
                required={isRequired('city')}
                disabled={loading || disableProjectLocationDescriptionInput}
                onBlur={() => {
                  getCoordinatesFromAddress();
                }}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} style={{ margin: theme.spacing(1) }}></Grid>
          <Grid item xs={12}>
            <Button
              className={classes.actionBtn}
              onClick={() => handleOpenMapDialog()}
              color="primary"
              fullWidth
              disabled={loading || loadingLocation}
              outline
            >
              {projectData.latitude && projectData.longitude
                ? t('project_dialog.button_open_map')
                : t('project_dialog.button_set_location_on_map')}
              <Icon
                path={
                  projectData.latitude && projectData.longitude
                    ? mdiMapCheck
                    : mdiMap
                }
                size={1}
              />
            </Button>
          </Grid>
          <SwitchItem
            title={t('project_dialog.toggle_location_required_on_clock_in')}
            name="locationMust"
            checked={projectData.locationMust}
            onChange={(checked) => handleLocationMustChange(checked)}
            disabled={loading}
          >
            <Grid container item spacing={1}>
              <Grid item xs={12}>
                <SwitchItem
                  title={t(
                    'project_dialog.toggle_clock_in_only_at_project_location'
                  )}
                  name="proximityToClockInRequired"
                  checked={projectData.proximityToClockInRequired}
                  onChange={(checked) =>
                    handleChangeNonEvent('proximityToClockInRequired', checked)
                  }
                  disabled={
                    loading || !hasProjectCoordinates || loadingLocation
                  }
                >
                  <Grid container item spacing={1}>
                    <Grid item xs={5}>
                      <Button
                        className={classes.actionBtn}
                        onClick={() => setOpenDrawCircleMapDialog(true)}
                        color="primary"
                        fullWidth
                        disabled={
                          loading ||
                          !projectData.latitude ||
                          !projectData.longitude ||
                          loadingLocation
                        }
                        outline={
                          projectData.proximityToClockInType !== 'radius'
                        }
                      >
                        {projectData.proximityToClockInRadius
                          ? t('project_dialog.text_radius_value', {
                              radius: projectData.proximityToClockInRadius,
                            })
                          : t('project_dialog.button_set_radius')}
                        <Icon
                          path={
                            projectData.proximityToClockInRadius
                              ? mdiCheck
                              : mdiMapMarkerRadiusOutline
                          }
                          size={1}
                        />
                      </Button>
                    </Grid>
                    <Grid
                      item
                      xs={2}
                      justifyContent="center"
                      alignItems="center"
                      container
                    >
                      <Grid>
                        <Typography>{t('project_dialog.text_or')}</Typography>
                      </Grid>
                    </Grid>
                    <Grid item xs={5}>
                      <Button
                        className={classes.actionBtn}
                        onClick={() => setOpenDrawAreaMapDialog(true)}
                        color="primary"
                        fullWidth
                        disabled={
                          loading ||
                          !projectData.latitude ||
                          !projectData.longitude ||
                          loadingLocation
                        }
                        outline={projectData.proximityToClockInType !== 'area'}
                      >
                        {projectData.proximityToClockInAreas?.length
                          ? t('project_dialog.text_areas_drawn', {
                              count: projectData.proximityToClockInAreas.length,
                            })
                          : t('project_dialog.button_draw_area')}
                        <Icon
                          path={
                            projectData.proximityToClockInAreas?.length
                              ? mdiCheck
                              : mdiVectorSquareEdit
                          }
                          size={1}
                        />
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <SwitchItem
                        title={t('project_dialog.toggle_allow_skip_override')}
                        name="proximityToClockInRequiredSkipAllowed"
                        checked={
                          projectData.proximityToClockInRequiredSkipAllowed
                        }
                        onChange={(checked) =>
                          handleChangeNonEvent(
                            'proximityToClockInRequiredSkipAllowed',
                            checked
                          )
                        }
                        disabled={loading}
                      />
                    </Grid>
                  </Grid>
                </SwitchItem>
              </Grid>
            </Grid>
          </SwitchItem>
        </ActionContainer>
        <ActionContainer // Yhteyshenkilön tiedot
          title={t('project_dialog.section_contact_information')}
          closable
          open={openContactInformation}
          setOpen={setOpenContactInformation}
          required={projectData.ilmoitettava}
        >
          <Grid container item spacing={1}>
            <Grid item xs={12} sm={6}>
              <ContactAutocomplete
                label={t('project_dialog.field_first_name')}
                name="contact_first_name"
                fullWidth
                options={contactOptions}
                value={projectData.contact_first_name}
                onChange={handleChange}
                required={isRequired('contact_first_name')}
                handleSelect={handleChangeContact}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextInput
                label={t('project_dialog.field_last_name')}
                name="contact_last_name"
                fullWidth
                value={projectData.contact_last_name}
                onChange={handleChange}
                required={isRequired('contact_last_name')}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextInput
                label={t('project_dialog.field_email')}
                name="contact_email"
                fullWidth
                value={projectData.contact_email}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextInput
                label={t('project_dialog.field_phone_number')}
                name="contact_phone"
                fullWidth
                value={projectData.contact_phone}
                onChange={handleChange}
                required={isRequired('contact_phone')}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} style={{ margin: theme.spacing(1) }}></Grid>
            <Grid item xs={12} sm={6}>
              <AddressAutocomplete
                label={t('project_dialog.field_street_address')}
                name="contact_street1"
                value={projectData.contact_street1}
                onChange={handleChange}
                handleSelect={handleChangeContactStreet1}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextInput
                label={t('project_dialog.field_street_address_2')}
                name="contact_street2"
                fullWidth
                value={projectData.contact_street2}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={6}>
              <TextInput
                label="C/O"
                name="contact_co"
                fullWidth
                value={projectData.contact_co}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={6}>
              <TextInput
                label={t('project_dialog.field_postal_code')}
                name="contact_postcode"
                fullWidth
                value={projectData.contact_postcode}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={6}>
              <TextInput
                label={t('project_dialog.field_city')}
                name="contact_city"
                fullWidth
                value={projectData.contact_city}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={6}>
              <TextInput
                label={t('project_dialog.field_country')}
                name="contact_country"
                fullWidth
                value={projectData.contact_country}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
          </Grid>
        </ActionContainer>
        {!companyMode && (
          <ActionContainer // Työnjohtajien tiedot
            title={t('project_dialog.section_manager_information')}
            closable
            open={openManagerInformation}
            setOpen={setOpenManagerInformation}
          >
            <Grid container item spacing={1}>
              <ActionContainer // Vastaavan työnjohtajan tiedot
                title={t('project_dialog.section_responsible_site_manager')}
                color="main"
              >
                <Grid item xs={12} sm={6}>
                  <ContactAutocomplete
                    label={t('project_dialog.field_first_name')}
                    name="site_manager_first_name"
                    fullWidth
                    fieldPrefix="site_manager"
                    options={siteManagerOptions}
                    value={projectData.site_manager_first_name}
                    onChange={handleChange}
                    required={isRequired('site_manager_first_name')}
                    handleSelect={handleChangeSiteManager}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextInput
                    label={t('project_dialog.field_last_name')}
                    name="site_manager_last_name"
                    fullWidth
                    value={projectData.site_manager_last_name}
                    onChange={handleChange}
                    required={isRequired('site_manager_last_name')}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextInput
                    label={t('project_dialog.field_email')}
                    name="site_manager_email"
                    fullWidth
                    value={projectData.site_manager_email}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextInput
                    label={t('project_dialog.field_phone_number')}
                    name="site_manager_phone"
                    fullWidth
                    value={projectData.site_manager_phone}
                    onChange={handleChange}
                    required={isRequired('site_manager_phone')}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} style={{ margin: theme.spacing(1) }}></Grid>
                <Grid item xs={12} sm={6}>
                  <AddressAutocomplete
                    label={t('project_dialog.field_street_address')}
                    name="site_manager_street1"
                    value={projectData.site_manager_street1}
                    onChange={handleChange}
                    handleSelect={handleChangeSiteManagerStreet1}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextInput
                    label={t('project_dialog.field_street_address_2')}
                    name="site_manager_street2"
                    fullWidth
                    value={projectData.site_manager_street2}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextInput
                    label="C/O"
                    name="site_manager_co"
                    fullWidth
                    value={projectData.site_manager_co}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextInput
                    label={t('project_dialog.field_postal_code')}
                    name="site_manager_postcode"
                    fullWidth
                    value={projectData.site_manager_postcode}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextInput
                    label={t('project_dialog.field_city')}
                    name="site_manager_city"
                    fullWidth
                    value={projectData.site_manager_city}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextInput
                    label={t('project_dialog.field_country')}
                    name="site_manager_country"
                    fullWidth
                    value={projectData.site_manager_country}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Grid>
              </ActionContainer>
              {projectData.foremanList.map((foreman) => {
                const id = foreman.id;
                return (
                  <ActionContainer // Työnjohtajan tiedot
                    title={`${t('project_dialog.text_foreman')} ${foreman.firstName} ${foreman.lastName}`}
                    color="main"
                    closable
                    open={openForemanListInformation[id]}
                    setOpen={() =>
                      setOpenForemanListInformation((prevState) => ({
                        ...prevState,
                        [id]: !prevState[id],
                      }))
                    }
                    key={id}
                    popperIcon={<Icon path={mdiDotsVertical} size={1} />}
                    popperChildren={
                      <Icon path={mdiDelete} size={1} color="red" />
                    }
                    onPopper={() => handleDeleteForeman(id)}
                  >
                    <Grid item xs={12} sm={6}>
                      <TextInput
                        label={t('project_dialog.field_first_name')}
                        name="firstName"
                        fullWidth
                        value={foreman.firstName}
                        onChange={(e) => handleChangeForeman(e, id)}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextInput
                        label={t('project_dialog.field_last_name')}
                        name="lastName"
                        fullWidth
                        value={foreman.lastName}
                        onChange={(e) => handleChangeForeman(e, id)}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextInput
                        label={t('project_dialog.field_email')}
                        name="email"
                        fullWidth
                        value={foreman.email}
                        onChange={(e) => handleChangeForeman(e, id)}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextInput
                        label={t('project_dialog.field_phone_number')}
                        name="phone"
                        fullWidth
                        value={foreman.phone}
                        onChange={(e) => handleChangeForeman(e, id)}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      style={{ margin: theme.spacing(1) }}
                    ></Grid>
                    <Grid item xs={12} sm={6}>
                      <TextInput
                        label={t('project_dialog.field_street_address')}
                        name="street1"
                        fullWidth
                        value={foreman.street1}
                        onChange={(e) => handleChangeForeman(e, id)}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextInput
                        label={t('project_dialog.field_street_address_2')}
                        name="street2"
                        fullWidth
                        value={foreman.street2}
                        onChange={(e) => handleChangeForeman(e, id)}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextInput
                        label="C/O"
                        name="co"
                        fullWidth
                        value={foreman.co}
                        onChange={(e) => handleChangeForeman(e, id)}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextInput
                        label={t('project_dialog.field_postal_code')}
                        name="postcode"
                        fullWidth
                        value={foreman.postcode}
                        onChange={(e) => handleChangeForeman(e, id)}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextInput
                        label={t('project_dialog.field_city')}
                        name="city"
                        fullWidth
                        value={foreman.city}
                        onChange={(e) => handleChangeForeman(e, id)}
                        disabled={loading}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <TextInput
                        label={t('project_dialog.field_country')}
                        name="country"
                        fullWidth
                        value={foreman.country}
                        onChange={(e) => handleChangeForeman(e, id)}
                        disabled={loading}
                      />
                    </Grid>
                  </ActionContainer>
                );
              })}

              <Grid item xs={12} sm={12}>
                <Button
                  className={classes.actionBtn}
                  onClick={() => handleAddForeman()}
                  color="primary"
                  fullWidth
                  disabled={loading}
                  style={{ marginTop: 10 }}
                >
                  <Icon path={mdiPlus} size={1} />
                  {t('project_dialog.button_add_new_manager')}
                </Button>
              </Grid>
            </Grid>
          </ActionContainer>
        )}
        {!companyMode && (
          <ActionContainer // Laskutusasiakkaan tiedot
            title={t('project_dialog.section_billing_customer_information')}
            closable
            open={openCustomerInformation}
            setOpen={setOpenCustomerInformation}
          >
            <Grid container item spacing={1}>
              <Grid item xs>
                <TextInput
                  label={t('project_dialog.field_customer')}
                  fullWidth
                  name="customer"
                  value={projectData.customer}
                  onChange={handleChange}
                  disabled={loading}
                  select
                >
                  {customerOptions?.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.name}{' '}
                      {customer.number && `(${customer.number})`}
                    </MenuItem>
                  ))}
                </TextInput>
              </Grid>
              <Grid item>
                <Button
                  color="primary"
                  onClick={() => openCustomerDialog()}
                  disabled={loading}
                >
                  {t('project_dialog.button_add_new_customer')}
                </Button>
              </Grid>
            </Grid>
          </ActionContainer>
        )}
        <ActionContainer // Lisäasetukset
          title={t('project_dialog.section_additional_information')}
          closable
          open={openAdditionalSettings}
          setOpen={setOpenAdditionalSettings}
        >
          <Grid container item>
            <TextInput
              label={t('project_dialog.field_tax_report_worksite_number')}
              name="costpoolNumber"
              fullWidth
              value={projectData.costpoolNumber}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} style={{ margin: theme.spacing(1) }}></Grid>
          <Grid container item>
            <TextInput
              label={t('project_dialog.field_iban_account_number')}
              name="iban"
              fullWidth
              value={projectData.iban}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>
          <Grid container item>
            <TextInput
              label="BIC"
              name="bic"
              fullWidth
              value={projectData.bic}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>
          {companyMode && (
            <SwitchItem
              title={t('project_dialog.toggle_invoice_project_scanning')}
              checked={projectData.enableInvoiceProjectScanning}
              onChange={(checked) =>
                handleChangeNonEvent('enableInvoiceProjectScanning', checked)
              }
              disabled={loading}
            />
          )}
          <Grid item xs={12} style={{ margin: theme.spacing(1) }}></Grid>
          {integrationPartners.includes('Fennoa') && (
            <Grid container item>
              <TextInput
                label={t('project_dialog.field_fennoa_dimension_id')}
                name="fennoaProjectDimensionId"
                fullWidth
                value={projectData.fennoaProjectDimensionId}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
          )}
          {integrationPartners.includes('TALENOM') && (
            <Grid container item>
              <TextInput
                label={t('project_dialog.field_talenom_dimension_id')}
                name="talenomProjectDimensionId"
                fullWidth
                value={projectData.talenomProjectDimensionId}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
          )}
          {integrationPartners.includes('PROCOUNTOR') && (
            <Grid container item>
              <TextInput
                label={t('project_dialog.field_procountor_dimension_id')}
                name="procountorDimension"
                fullWidth
                value={projectData.procountorDimension}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
          )}
          {integrationPartners.includes('JYDACOM') && (
            <Grid container item>
              <TextInput
                label={t('project_dialog.field_jydacom_worksite_id')}
                name="jydacomWorksiteId"
                fullWidth
                value={projectData.jydacomWorksiteId}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
          )}
          {integrationPartners.includes('TALOSOFTA') && (
            <Grid container item>
              <TextInput
                label={t('project_dialog.field_talosofta_project_identifier')}
                name="talosoftaProjectId"
                fullWidth
                value={projectData.talosoftaProjectId}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
          )}
          {/* {integrationPartners.includes('TALENOM') && (
            <Grid container item>
              <TextInput
                label="Talenom työmaatunniste"
                name="talenomProjectId"
                fullWidth
                value={projectData.talenomProjectId}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
          )} */}
          {type === 'edit' && !companyMode && (
            <>
              <Grid item xs={12} style={{ margin: theme.spacing(1) }}></Grid>
              <Grid container item spacing={1} justifyContent="space-between">
                <Grid item>
                  <Grid container item spacing={1}>
                    <Grid item>
                      <Button
                        className={classes.actionBtn}
                        onClick={() => setOpenDeleteConfirmationDialog(true)}
                        color="danger"
                        disabled={loading}
                      >
                        <Icon path={mdiDelete} size={1} />
                        {t('project_dialog.button_delete_project')}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <Button
                    className={classes.actionBtn}
                    onClick={() => {}}
                    color="primary"
                    disabled={loading}
                    outline
                  >
                    <Icon path={mdiMerge} size={1} />
                    {t('project_dialog.button_merge_projects')}
                  </Button>
                </Grid>
              </Grid>
            </>
          )}
        </ActionContainer>
        <ActionContainer // Litterat
          title={t('project_dialog.section_assignments')}
          closable
          open={openAssignments}
          setOpen={setOpenAssignments}
        >
          {assignmentCopyInfoOpen && type === 'create' && (
            <Grid item xs={12}>
              <Alert
                severity="info"
                variant="outlined"
                // onClose={() => setAssignmentCopyInfoOpen(false)}
              >
                <AlertTitle>
                  {t('project_dialog.alert_notice_title')}
                </AlertTitle>
                {t('project_dialog.alert_assignments_copied_from_host_project')}
              </Alert>
            </Grid>
          )}
          {hasValidAssignments &&
            assignmentCopyWarningOpen &&
            !project.hasOwnAssignments && (
              <Grid item xs={12}>
                <Alert
                  severity="warning"
                  variant="outlined"
                  // onClose={() => setAssignmentCopyWarningOpen(false)}
                >
                  <AlertTitle>
                    {t('project_dialog.alert_warning_title')}
                  </AlertTitle>
                  {t(
                    'project_dialog.alert_assignments_changed_from_host_project'
                  )}
                </Alert>
              </Grid>
            )}
          {!hasValidAssignments && (
            <Grid item xs={12}>
              <Alert
                severity="error"
                variant="outlined"
                // onClose={() => setAssignmentCopyWarningOpen(false)}
              >
                <AlertTitle>
                  {t('project_dialog.alert_notice_title')}
                </AlertTitle>
                {t(
                  'project_dialog.alert_assignments_missing_required_information'
                )}
                <br />
                &nbsp;
              </Alert>
            </Grid>
          )}
          {activeAssignments.length > 0 ? (
            <Grid container item spacing={1} justifyContent="flex-end">
              <Grid item xs="auto">
                {openAssignmentsArray.length ? (
                  <Button
                    className={classes.actionBtn}
                    onClick={() => setOpenAssignmentsArray([])}
                    color="primary"
                    disabled={loading}
                    outline
                  >
                    {t('project_dialog.button_close_all')}
                    <Icon path={mdiChevronUp} size={1} />
                  </Button>
                ) : (
                  <Button
                    className={classes.actionBtn}
                    onClick={() =>
                      setOpenAssignmentsArray(
                        activeAssignments.map((a) => a.id)
                      )
                    }
                    color="primary"
                    disabled={loading}
                    outline
                  >
                    {t('project_dialog.button_open_all')}
                    <Icon path={mdiChevronDown} size={1} />
                  </Button>
                )}
              </Grid>
            </Grid>
          ) : null}
          {activeAssignments
            .filter((assignment) => !assignment.parent)
            .sort((a, b) => a.number - b.number)
            .map((assignment) => (
              <Grid container item key={assignment.id}>
                <AssignmentItem
                  assignment={assignment}
                  childAssignments={activeAssignments
                    .filter((child) => child.parent === assignment.id)
                    .sort((a, b) => a?.number - b?.number)}
                  assignments={activeAssignments}
                  handleChangeAssignment={handleChangeAssignment}
                  handleChangeValueOfAssignmentAndAllChildren={
                    handleChangeValueOfAssignmentAndAllChildren
                  }
                  handleAddAssignment={handleAddAssignment}
                  showAssignmentNumberPath={showAssignmentNumberPath}
                  handleDeleteAssignment={handleDeleteAssignment}
                  openAssignmentsArray={openAssignmentsArray}
                  setOpenAssignmentsArray={setOpenAssignmentsArray}
                  updatedAssignmentIds={updatedAssignmentIds}
                />
              </Grid>
            ))}
          <Grid item xs={12} style={{ margin: theme.spacing(1) }}></Grid>
          <Grid container item spacing={1} justifyContent="flex-end">
            <Grid item xs={12} sm={12}>
              <Button
                className={classes.actionBtn}
                onClick={() => handleAddAssignment()}
                color="primary"
                fullWidth
                disabled={loading}
              >
                <Icon path={mdiPlus} size={1} />
                {t('project_dialog.button_add_new')}
              </Button>
            </Grid>
          </Grid>
          <Grid container item spacing={1}>
            <Grid item xs={12} sm={4}>
              <Button
                className={classes.actionBtn}
                onClick={() => handleWriteXLS()}
                color="primary"
                fullWidth
                disabled={loading}
                outline
              >
                <Icon path={mdiTableArrowDown} size={1} />
                {t('project_dialog.button_export_xls')}
              </Button>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                className={classes.actionBtn}
                color="primary"
                fullWidth
                disabled={loading}
                outline
                component="label"
              >
                <Icon path={mdiTableArrowUp} size={1} />
                {t('project_dialog.button_import_xls')}
                <input type="file" hidden onChange={handleReadXLS} />
              </Button>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                className={classes.actionBtn}
                onClick={() => handleDownloadXLSBase()}
                color="primary"
                fullWidth
                disabled={loading}
                outline
              >
                <Icon path={mdiDownload} size={1} />
                {t('project_dialog.button_download_template')}
              </Button>
            </Grid>
          </Grid>

          <SwitchItem
            title={t('project_dialog.toggle_show_assignment_number_path')}
            checked={showAssignmentNumberPath}
            onChange={(checked) => setShowAssignmentNumberPath(checked)} // CHANGE
            disabled={loading}
          />

          <Grid container item spacing={1} justifyContent="space-between">
            <Grid item>
              <Button
                className={classes.actionBtn}
                onClick={() => handleDeleteAllAssignments()}
                color="danger"
                fullWidth
                disabled={loading}
              >
                <Icon path={mdiDelete} size={1} />
                {t('project_dialog.button_delete_all')}
              </Button>
            </Grid>
            {integrationPartners.includes('JYDACOM') ? (
              <Grid item>
                <Button
                  className={classes.actionBtn}
                  onClick={() =>
                    dispatch(setIsJydacomAssignmentsDialogOpen(true))
                  }
                  color="primary"
                  disabled={loading}
                  outline
                  fullWidth
                >
                  <Icon path={mdiMerge} size={1} />
                  {t('project_dialog.button_merge_jydacom_assignments')}
                </Button>
              </Grid>
            ) : null}
          </Grid>
        </ActionContainer>
        {/* {!companyMode && (
          <FirestopCatalogsSection
            loading={loading}
            isLoadingFirestopDetails={isLoadingFirestopDetails}
            firestopDetails={firestopDetails}
            onAddFirestopDetail={handleAddFirestopDetail}
            onChangeFirestopDetail={handleChangeFirestopDetail}
            onDeleteFirestopDetail={handleDeleteFirestopDetail}
            isLoadingFirestopProducts={isLoadingFirestopProducts}
            firestopProducts={firestopProducts}
            onAddFirestopProduct={handleAddFirestopProduct}
            onChangeFirestopProduct={handleChangeFirestopProduct}
            onDeleteFirestopProduct={handleDeleteFirestopProduct}
          />
        )} */}
        {companyMode && isPermissionToEditSalaryTypes ? (
          <ActionContainer // Palkkatiedot
            title={t('project_dialog.section_salary_types')}
            closable
            open={openSalaryInformation}
            setOpen={setOpenSalaryInformation}
          >
            <EmployerAllowances></EmployerAllowances>
            <Grid item xs={12} style={{ margin: theme.spacing(1) }}></Grid>
            <Grid container item spacing={1} justifyContent="flex-end">
              <Grid item xs={12} sm={12}>
                <Button
                  className={classes.actionBtn}
                  onClick={() => handleAddEmployerAllowanceDraft()}
                  color="primary"
                  fullWidth
                  disabled={loading}
                >
                  <Icon path={mdiPlus} size={1} />
                  {t('project_dialog.button_add_new')}
                </Button>
              </Grid>
            </Grid>
          </ActionContainer>
        ) : null}
        <ProjectTradeRatesSection
          open={openTradeRates}
          setOpen={setOpenTradeRates}
          loading={loading}
          hourlyCosts={projectData.tradeHourlyCosts}
          onChangeHourlyCost={handleChangeTradeHourlyCost}
          inherited={tradeRatesInherited}
          changedFromParentWarningOpen={tradeRatesChangedFromParentWarningOpen}
        />
        {companyMode && (
          <ActionContainer // Hälytysasetukset
            title={t('project_dialog.section_notification_settings')}
            closable
            open={openNotificationSettings}
            setOpen={setOpenNotificationSettings}
          >
            <AlarmSettings></AlarmSettings>
          </ActionContainer>
        )}
        {companyMode && (
          <ProjectCompetencyTypesSection
            loading={loading}
            isLoadingCompetencyTypes={props.isLoadingBusinessCompetencyTypes}
            open={openCompetencyTypesInformation}
            setOpen={setOpenCompetencyTypesInformation}
            competencyTypes={competencyTypes}
            onAddCompetencyType={handleAddCompetencyType}
            onChangeCompetencyType={handleChangeCompetencyType}
            onDeleteCompetencyType={handleDeleteCompetencyType}
          />
        )}
        {/* {companyMode && (
          <ActionContainer // Kirjautumisasetukset
            title={t('project.clock_in_settings')}
            closable
            open={openClockInSettings}
            setOpen={setOpenClockInSettings}
          >
            <ClockInSettings
              projectData={projectData}
              handleChangeNonEvent={handleChangeNonEvent}
              loading={loading}
            ></ClockInSettings>
          </ActionContainer>
        )} */}
      </DialogContent>
      <Divider />
      <DialogActions className={classes.dialogActions}>
        <Typography>{t('project_dialog.required_fields_note')}</Typography>
        <Grid>
          {loading && <ButtonLoading />}
          <Button
            className={classes.actionBtn}
            onClick={() => handleSave()}
            color="dark"
            disabled={loading || isSaveDisabled}
          >
            {type === 'create'
              ? showCustomerTextInsteadOfProject
                ? t('project_dialog.button_create_customer')
                : t('project_dialog.button_create_project')
              : t('project_dialog.button_save')}
          </Button>
        </Grid>
      </DialogActions>
    </Dialog>
  );
}

ProjectDialogLayout.defaultProps = {
  project: {},
  projects: [],
  parentProject: null,
  parentObject: {},
  assignments: [],
  projectCompetencyTypes: [],
  type: 'create',
  companyMode: false,
  loading: false,
  isLoadingBusinessCompetencyTypes: false,
  customerOptions: [],
};

ProjectDialogLayout.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  project: PropTypes.object.isRequired,
  projects: PropTypes.array,
  parentProject: PropTypes.string,
  parentObject: PropTypes.object,
  assignments: PropTypes.array,
  projectCompetencyTypes: PropTypes.array,
  type: PropTypes.oneOf(['create', 'edit']),
  companyMode: PropTypes.bool,
  loading: PropTypes.bool,
  isLoadingBusinessCompetencyTypes: PropTypes.bool,
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  customerOptions: PropTypes.array,
  openCustomerDialog: PropTypes.func,
};

export default ProjectDialogLayout;
